import { Input, Text, Box, Divider } from 'theme-ui';
import React, { FC, HTMLAttributes, useEffect, useState } from 'react';
import { groupBy, merge } from 'lodash';
import Icon from '@solid-ui-components/ContentIcon';
import Search from '../../assets/icons/search.svg';
import Close from '../../assets/icons/close.svg';
import { Link } from 'gatsby';
import { CSSProperties } from '@emotion/serialize';

const styles = {
  root: {
    marginBottom: 40,
    position: 'relative',
    '@media (max-width: 1024px)': {
      marginTop: 40,
    },
  },
  box: {
    boxShadow: '0px 2px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: 20,
    border: 0,
    padding: '10px 20px',
    backgroundColor: 'white',
    width: '100%',
  },
  inner: {
    display: 'flex',
    flexDirection: 'row',
  },
  input: {
    flex: 1,
    fontSize: '1rem',
    padding: 0,
    outline: 'none !important',
    borderRadius: 0,
  },
  button: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    marginBottom: 10,
    color: 'alpha',
  },
  icon: {
    width: 20,
    fill: '#aaa',
    fontWeight: 'bold',
  },
  searchResult: {
    marginTop: 20,
    maxHeight: 400,
    overflowY: 'scroll',
    position: 'absolute',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  divider: {
    margin: '15px 0',
  },
};

export type ClassKeys = keyof typeof styles;

export type CategoryType = {
  name: string;
  slug: string;
};

export type DataType = {
  slug: string;
  title: string;
  category: CategoryType;
};

export type SearchInputProps = {
  onChange: HTMLAttributes<HTMLInputElement>['onChange'];
  placeholder?: string;
  onReset: () => void;
  value: string;
  classes?: Partial<Record<ClassKeys, CSSProperties>>;
  data?: DataType[];
};

const SearchInput: FC<SearchInputProps> = ({
  onChange,
  placeholder,
  onReset,
  value,
  classes: newStyles = {},
  data,
}) => {
  const [focus, setFocus] = useState(false);
  const [groups, setGroups] = useState([]);
  merge(styles, newStyles);

  useEffect(() => {
    if (data) {
      const filteredData = data.filter(({ title }) => title.includes(value));
      setGroups(groupBy(filteredData, 'category.name'));
    }
  }, [data, value]);

  const handleBlur = () => {
    setTimeout(() => {
      setFocus(false);
    }, 400);
  };

  return (
    <Box
      as='form'
      onSubmit={(e) => e.preventDefault()}
      onReset={onReset}
      sx={styles.root}
    >
      <Box sx={{ ...styles.inner, ...styles.box }}>
        <Input
          as='input'
          onChange={onChange}
          type='text'
          placeholder={placeholder || 'Enter a keyword'}
          sx={styles.input}
          onFocus={() => setFocus(true)}
          onBlur={handleBlur}
        />
        <Box
          as='button'
          className='icon'
          sx={styles.button}
          type={!value ? 'submit' : 'reset'}
        >
          <Icon
            className='icon'
            content={{
              src: !value ? Search : Close,
              alt: !value ? 'Search' : 'Close',
            }}
            sx={styles.icon}
            size='xs'
          />
        </Box>
      </Box>
      {focus && Object.keys(groups).length > 0 && value && (
        <Box sx={{ ...styles.box, ...styles.searchResult }}>
          {Object.entries(groups).map(([value, entry], index) => (
            <Box>
              <Text variant='h3'>{value}</Text>
              <Box>
                {entry.map(({ title, slug, category }) => (
                  <Link to={`/support/${category.slug}/${slug}`}>
                    <Text sx={styles.text}>{title}</Text>
                  </Link>
                ))}
              </Box>
              {Object.entries(groups).length - 1 !== index && (
                <Divider sx={styles.divider} />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SearchInput;
